/**
 * WCAG 대비 계산 + 명도 보정 유틸 (순수 함수, 외부 의존 없음)
 *
 * 등락 색 토큰(utils/trend.js, ThemeContext)이 테마 paper 배경 대비 4.5:1 이상이
 * 되도록 hue/saturation 은 유지한 채 lightness 만 보정할 때 사용한다.
 */

const clamp01 = (v) => Math.min(1, Math.max(0, v));

export const normalizeHex = (hex) => {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return `#${h.toUpperCase()}`;
};

const hexToRgb = (hex) => {
  const h = normalizeHex(hex).slice(1);
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};

const toLinear = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** WCAG 상대 휘도 */
export const relativeLuminance = (hex) => {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG 대비비 (1 ~ 21) */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

export const hexToHsl = (hex) => {
  const [r, g, b] = hexToRgb(hex).map((c) => c / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h / 6, s, l };
};

export const hslToHex = ({ h, s, l }) => {
  const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  let r;
  let g;
  let b;
  if (s === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const to = (v) => Math.round(clamp01(v) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
};

/**
 * 모든 배경(bgs)에 대해 minRatio 이상이 되도록 lightness 를 한 방향으로 보정한다.
 * 이미 만족하면 원본을 그대로 돌려준다.
 *
 * @param {string} color  보정할 hex
 * @param {string[]} bgs  대비를 확보할 배경 hex 목록
 * @param {number} minRatio  최소 대비비 (텍스트 4.5)
 * @param {'darken'|'lighten'} direction  보정 방향 (라이트 테마=darken, 다크 테마=lighten)
 */
export const ensureContrast = (color, bgs, minRatio = 4.5, direction = 'darken') => {
  const base = normalizeHex(color);
  const backgrounds = bgs.map(normalizeHex).filter(Boolean);
  if (!base || backgrounds.length === 0) return color;
  const ok = (c) => backgrounds.every((bg) => contrastRatio(c, bg) >= minRatio);
  if (ok(base)) return base;

  const hsl = hexToHsl(base);
  const step = direction === 'darken' ? -0.01 : 0.01;
  let l = hsl.l;
  for (let i = 0; i < 100; i += 1) {
    l += step;
    if (l < 0 || l > 1) break;
    const candidate = hslToHex({ ...hsl, l });
    if (ok(candidate)) return candidate;
  }
  // 보정 불가(극단적 배경) — 가장 대비가 큰 흑/백으로 폴백
  return direction === 'darken' ? '#000000' : '#FFFFFF';
};
